/* eslint-disable @typescript-eslint/no-explicit-any */
import { FormControl, FormGroup } from '@angular/forms';

export const simpleDeepClone = <T>(value: T): T => JSON.parse(JSON.stringify(value));
export const omitKey = <T, K extends keyof T>(obj: T, key: K): Omit<T, K> => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { [key]: value, ...rest } = obj;
  return rest;
};

// based on cloneDeep source code from lodash
export const cloneDeep = (value: any, clonedObjects = new WeakMap()): unknown => {
  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (clonedObjects.has(value)) {
    return clonedObjects.get(value);
  }

  let clone: any;

  if (value instanceof Date) {
    return new Date(value);
  } else if (Array.isArray(value)) {
    clone = [];
    clonedObjects.set(value, clone);
    value.forEach((item, index) => {
      clone[index] = cloneDeep(item, clonedObjects);
    });
  } else if (value instanceof RegExp) {
    clone = new RegExp(value);
  } else {
    clone = Object.create(Object.getPrototypeOf(value));
    clonedObjects.set(value, clone);
    for (const key in value) {
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        clone[key] = cloneDeep(value[key], clonedObjects);
      }
    }
  }
  return clone;
};

export const isTheSameData = <T>(obj1: T, obj2: T): boolean => {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
};

export const isEncodedAgreement = (agreementNumber: string): boolean => {
  return agreementNumber !== decodeURIComponent(agreementNumber);
};

export const isEmpty = (value: null | undefined | string) => {
  return value == null || (typeof value === 'string' && value.trim().length === 0);
};

export const formatDateToDDMMYY = (dateParam: Date | string): string => {
  const date = new Date(dateParam);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
  const year = String(date.getFullYear()).slice(-2); // Get last two digits of the year
  return `${day}/${month}/${year}`;
};

export const getFormControlByName = (form: FormGroup, name: string): FormControl => {
  return form.get(name) as FormControl;
};

/**
 * Capitalizes the first letter of a string.
 * @param word - The string to capitalize
 * @returns The string with first letter capitalized
 */
export const capitalizeFirstLetter = (word: string): string => {
  if (!word || typeof word !== 'string') return '';
  return word.charAt(0).toUpperCase() + word.slice(1);
};

/**
 * Converts a string to title case (capitalizes first letter of each word).
 * @param str - The string to convert
 * @returns The string in title case
 */
export const toTitleCase = (str: string): string => {
  if (!str || typeof str !== 'string') return '';
  return str
    .split(' ')
    .filter((word) => word.length > 0)
    .map(capitalizeFirstLetter)
    .join(' ');
};
